import React, { useState, useContext, useEffect, useRef } from "react";
import {
  LoadingOverlay,
  Text,
  Button,
  Group as MantineGroup,
  Stack,
  Box,
  Alert,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import EnglishLoadingWithCompletion from "./english-loading-with-completion";
import DataCollectionIntro from "./data-collection-intro";
import FormField from "./form-field";
import { DataContext } from "../context/DataContext";
import useStatusCheck from "../hooks/useStatusCheck";
import useEdgeStatusCheck from "../hooks/useEdgeStatusCheck";
import useAdjustInputSize from "../hooks/useAdjustInputSize";

const CoppaForm = ({
  title,
  subtitle,
  icon,
  componentKey,
  endpoint,
  config,
  buttonText,
  reAttempts,
  onApiResponse,
}) => {
  const { data, text, dispatch, settings } = useContext(DataContext);
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(reAttempts);
  const [statusResponse, setStatusResponse] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasReceivedFailedResponse, setHasReceivedFailedResponse] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [showMoreFields, setShowMoreFields] = useState(false);
  const [processingVerification, setProcessingVerification] = useState(false);
  const [loaderComplete, setLoaderComplete] = useState(false);
  const inputSize = useAdjustInputSize();
  const topBarRef = useRef(null);
  const [topBarHeight, setTopBarHeight] = useState(0);
  const additionalFieldsRef = useRef(null);
  const [additionalHeight, setAdditionalHeight] = useState(0);
  const nameFieldsRef = useRef(null);
  const [nameFieldsHeight, setNameFieldsHeight] = useState(0);
  const [nameFieldsVisible, setNameFieldsVisible] = useState(false);
  const bounceTransition = "cubic-bezier(0.68, -0.55, 0.27, 1.55)";
  const lastConfirmationRef = useRef(null);
  const confirmationsReceivedRef = useRef([]);
  const waitingForConfirmationChangeRef = useRef(false);
  const initialLoadRef = useRef(true);
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [hasShownFormForCurrentAttempt, setHasShownFormForCurrentAttempt] = useState(false);

  const buttonContentColor =
    data.styles?.components?.button?.color ||
    (theme.colorScheme === "dark" ? theme.white : theme.black);

  const initialVisibleFields = config.initiallyVisibleFields || config.fields.length;

  // Group fields for expandable sections
  const groupedFields = config.fields.reduce(
    (acc, field) => {
      if (field.group) {
        if (!acc.grouped[field.group]) {
          acc.grouped[field.group] = [];
        }
        acc.grouped[field.group].push(field);
      } else {
        acc.ungrouped.push(field);
      }
      return acc;
    },
    { grouped: {}, ungrouped: [] },
  );

  const primaryFields = groupedFields.ungrouped.slice(0, initialVisibleFields);
  const additionalFields = groupedFields.ungrouped.slice(initialVisibleFields);

  // Validation functions
  const isNameFieldsValid = () => {
    const nameFields = config.fields.filter(
      (field) => field.name === "fn" || field.name === "ln",
    );
    return nameFields.every((field) => {
      const value = form.values[field.name] || "";
      return field.validation ? !field.validation(value) : true;
    });
  };

  const isCoppaDetailsValid = () => {
    if (!showMoreFields) return true;
    const isDobComplete = form.values.dob?.length === 8 && !form.errors.dob;
    const isSsnComplete = form.values.ssn?.length === 4 && !form.errors.ssn;
    return isDobComplete && isSsnComplete;
  };

  // Handle expandable animations
  useEffect(() => {
    if (additionalFieldsRef.current) {
      setAdditionalHeight(additionalFieldsRef.current.scrollHeight);
    }
  }, []);

  useEffect(() => {
    if (showMoreFields && additionalFieldsRef.current) {
      setAdditionalHeight(additionalFieldsRef.current.scrollHeight);
    }
  }, [showMoreFields]);

  useEffect(() => {
    if (nameFieldsRef.current) {
      setNameFieldsHeight(nameFieldsRef.current.scrollHeight);
    }
  }, [showMoreFields]);

  useEffect(() => {
    if (!showIntro) {
      setNameFieldsVisible(true);
    }
  }, [showIntro]);

  useEffect(() => {
    if (topBarRef.current) {
      setTopBarHeight(topBarRef.current.offsetHeight);
    }
  }, [showIntro]);

  // Scroll to appropriate section when toggling between field groups
  useEffect(() => {
    if (showMoreFields) {
      if (additionalFieldsRef.current) {
        additionalFieldsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    } else {
      if (nameFieldsRef.current) {
        nameFieldsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [showMoreFields]);

  // Reset to name fields on reattempt
  useEffect(() => {
    if (attemptsLeft < reAttempts && hasReceivedFailedResponse) {
      setShowMoreFields(false);
      form.clearErrors();
      if (nameFieldsRef.current) {
        nameFieldsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      setHasReceivedFailedResponse(false);
    }
  }, [attemptsLeft, reAttempts, hasReceivedFailedResponse]);

  // Handle ResizeObserver for dynamic height calculations
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      if (additionalFieldsRef.current) {
        setAdditionalHeight(additionalFieldsRef.current.scrollHeight);
      }
      if (nameFieldsRef.current) {
        setNameFieldsHeight(nameFieldsRef.current.scrollHeight);
      }
    });

    if (additionalFieldsRef.current) observer.observe(additionalFieldsRef.current);
    if (nameFieldsRef.current) observer.observe(nameFieldsRef.current);

    return () => observer.disconnect();
  }, [showMoreFields]);

  // Edge mode status handler
  const handleEdgeStatusResult = (statusResult) => {
    setStatusResponse(statusResult);

    if (statusResult.status === "IN PROGRESS") {
      setLoading(true);
      return true;
    }

    if (statusResult.status === "PASS") {
      dispatch({ type: "SET_STATUS", payload: "PASS" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      setLoading(true);
      setProcessingVerification(false);
      return false;
    }

    if (statusResult.status === "FAIL") {
      dispatch({ type: "SET_STATUS", payload: "FAIL" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      setLoading(true);
      setProcessingVerification(false);
      return false;
    }

    if (statusResult.status === "PENDING") {
      if (waitingForConfirmationChangeRef.current) {
        waitingForConfirmationChangeRef.current = false;
      }

      if (!hasShownFormForCurrentAttempt && currentAttempt === 0) {
        setCurrentAttempt(1);
        setHasShownFormForCurrentAttempt(true);
        setAttemptsLeft(reAttempts);
        setLoading(false);
        setResult(false);
        return false;
      }

      if (attemptsLeft > 0) {
        setHasShownFormForCurrentAttempt(true);
        setLoading(false);
        setProcessingVerification(false);
        setHasReceivedFailedResponse(true);
        setResult(false);
        return false;
      }

      if (attemptsLeft === 0) {
        dispatch({ type: "SET_STATUS", payload: "FAIL" });
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
        setLoading(true);
        setProcessingVerification(false);
        return false;
      }
    }

    return true;
  };

  // Standard status check handler
  const handleStatusResult = (statusResult, confirmationChanged) => {
    if (initialLoadRef.current && statusResult) {
      initialLoadRef.current = false;
    }

    setStatusResponse(statusResult);
    const confirmationNumber = statusResult.confirmation || "unknown";

    if (statusResult.status === "FAIL" && !hasShownFormForCurrentAttempt && initialLoadRef.current) {
      initialLoadRef.current = false;
      setCurrentAttempt(1);
      setHasShownFormForCurrentAttempt(true);
      setAttemptsLeft(reAttempts);
      setLoading(false);
      setResult(false);
      return false;
    }

    if (statusResult.status === "PASS") {
      try {
        dispatch({ type: "SET_STATUS", payload: "PASS" });
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      } catch (err) {
        // Silent fail
      }
      setLoading(true);
      setProcessingVerification(false);
      return true;
    }

    if (waitingForConfirmationChangeRef.current && confirmationChanged) {
      waitingForConfirmationChangeRef.current = false;
      lastConfirmationRef.current = confirmationNumber;
      confirmationsReceivedRef.current.push(confirmationNumber);
      setHasShownFormForCurrentAttempt(false);

      if (statusResult.status !== "PASS" && attemptsLeft > 0) {
        setLoading(false);
        setProcessingVerification(false);
        setHasReceivedFailedResponse(true);
        setResult(false);
        return false;
      }

      if (statusResult.status !== "PASS" && attemptsLeft === 0) {
        try {
          dispatch({ type: "SET_STATUS", payload: "FAIL" });
          dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
        } catch (err) {
          // Silent fail
        }
      }

      setLoading(true);
      return true;
    }

    try {
      dispatch({ type: "SET_STATUS", payload: "PENDING" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
    } catch (err) {
      // Silent fail
    }

    setLoading(true);

    if (!result) {
      setResult(true);
    }

    return true;
  };

  // Determine if we should use edge mode
  const isEdgeMode = data?.edge === true;

  // Status check hooks
  useStatusCheck(!isEdgeMode && result, false, componentKey, handleStatusResult);
  useEdgeStatusCheck(isEdgeMode && result, handleEdgeStatusResult);

  // Form setup
  const form = useForm({
    initialValues: config.fields.reduce((acc, field) => {
      if (field.type === "address") {
        acc["addr"] = data.addr || "";
        acc["city"] = data.city || "";
        acc["state"] = data.state || "";
        acc["zip"] = data.zip || "";
      } else {
        acc[field.name] = data[field.name] || field.initialValue || "";
      }
      return acc;
    }, {}),
    validateInputOnChange: true,
    validateInputOnBlur: true,
    validate: (values) => {
      const errors = {};
      config.fields.forEach((field) => {
        if (field.validation) {
          // Skip validation for DOB and SSN until "Verify my identity" is clicked
          if (!showMoreFields && (field.name === "dob" || field.name === "ssn")) {
            return;
          }

          let error;
          if (field.name === "address" && field.type === "address") {
            error = field.validation({
              addr: values.addr || "",
              city: values.city || "",
              state: values.state || "",
              zip: values.zip || "",
            });
          } else {
            error = field.validation(values[field.name]);
          }
          if (error) {
            if (field.type === "address") {
              errors.addr = error;
            } else {
              errors[field.name] = error;
            }
          }
        }
      });
      return errors;
    },
  });

  // Submit handler
  const submitHandler = async () => {
    setIsVerifying(true);
    const errors = form.validate();

    if (errors.hasErrors) {
      setIsVerifying(false);
      return;
    }

    const newAttemptsLeft = attemptsLeft - 1;
    setAttemptsLeft(newAttemptsLeft);

    Object.keys(form.values).forEach((key) => {
      data[key] = form.values[key];
    });

    if (!data.settings) data.settings = {};
    data.settings.currentAttempt = currentAttempt;
    data.settings.attemptsLeft = newAttemptsLeft;
    data.attemptsLeft = newAttemptsLeft;

    setLoading(true);

    try {
      let apiUrl;
      let requestData = { ...data };

      if (data?.edge === true) {
        apiUrl = process.env.REACT_APP_EDGE_QUEUE_URL;
        requestData.queue = "coppa";
      } else {
        apiUrl = "https://vx.dcams.app/frontend/idmatch/coppa";
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      })
        .then((res) => res.json())
        .then((responseData) => {
          if (typeof onApiResponse === "function") {
            onApiResponse(responseData);
          }

          try {
            dispatch({ type: "SET_STATUS", payload: "PENDING" });
            dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
          } catch (err) {
            // Silent fail
          }

          setTimeout(() => {
            setLoading(true);
            setProcessingVerification(true);
            setLoaderComplete(false);
            setIsVerifying(false);
          }, 2000);

          setCurrentAttempt((prevAttempt) => prevAttempt + 1);

          if (isEdgeMode) {
            setHasShownFormForCurrentAttempt(false);
          }

          waitingForConfirmationChangeRef.current = true;
          // Don't activate status check until loader is done
        });
    } catch (error) {
      alert("Something Went Wrong! Please try again.");
      setIsVerifying(false);
    }
  };

  // Start status check when loader completes
  useEffect(() => {
    if (loaderComplete && processingVerification) {
      console.log("🏁 COPPA loader complete, starting status check");
      setResult(true);
      setLoaderComplete(false);
    }
  }, [loaderComplete, processingVerification]);

  const handleDobChange = (dob) => {
    const formattedDob = `${dob.year}${dob.month}${dob.day}`;
    form.setFieldValue("dob", formattedDob);
  };

  const handleAddressChange = (address) => {
    form.setFieldValue("addr", address.addr);
    form.setFieldValue("city", address.city);
    form.setFieldValue("state", address.state);
    form.setFieldValue("zip", address.zip);
  };

  // Initial load effect
  useEffect(() => {
    setCurrentAttempt(0);
    setHasShownFormForCurrentAttempt(false);
    setAttemptsLeft(reAttempts);
    setLoading(false);
    form.clearErrors();
  }, [componentKey]);

  // Check if browser language is English for loading component
  const browserLanguage = typeof navigator !== "undefined" ? navigator.language : "";
  const isEnglish = browserLanguage.toLowerCase().startsWith("en");

  if (loading && !isVerifying) {
    if (processingVerification && isEnglish) {
      const verificationMessages = [
        "Verifying identity...",
        "Checking personal information...",
        "Validating date of birth...",
        "Processing verification request...",
        "Finalizing identity check...",
      ];

      return (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 9999,
            backgroundColor:
              theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ flex: 1, display: "flex" }}>
            <EnglishLoadingWithCompletion
              messages={verificationMessages}
              finalMessageDuration={4000}
              alwaysAnimateFinalStep={true}
              onComplete={() => {
                console.log("✅ COPPA verification loader animation complete");
                setLoaderComplete(true);
              }}
            />
          </Box>
        </Box>
      );
    }
    return <LoadingOverlay visible />;
  }

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes slideUpBounce {
            0% {
              opacity: 0;
              transform: translateY(20px);
            }
            60% {
              opacity: 1;
              transform: translateY(-5px);
            }
            80% {
              transform: translateY(3px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <form
        onSubmit={form.onSubmit(submitHandler)}
        style={{
          display: "flex",
          flexDirection: "column",
          animation: "fadeIn 0.6s ease-out",
          minHeight: "100%",
          paddingBottom: "40px",
        }}
      >
        {!showIntro && (
          <Box
            ref={topBarRef}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              zIndex: 5,
              width: "100%",
              backgroundColor:
                theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
              borderBottom: `1px solid ${
                theme.colorScheme === "dark"
                  ? theme.colors.dark[5]
                  : theme.colors.gray[3]
              }`,
              padding: "4px 0",
            }}
          >
            <Text
              fz="sm"
              fw={700}
              ta="center"
              c={theme.colorScheme === "dark" ? theme.colors.gray[2] : theme.black}
            >
              {showMoreFields ? "Date of birth & SSN" : "Name details"}
            </Text>
          </Box>
        )}

        <Box
          style={{
            marginTop: !showIntro ? topBarHeight : 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {showIntro && (
            <DataCollectionIntro
              icon={icon}
              title={title}
              componentKey={componentKey}
              inputSize={inputSize}
              data={data}
              onStart={() => setShowIntro(false)}
              bounceTransition={bounceTransition}
              theme={theme}
            />
          )}

          {!showIntro && (
            <Box
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Stack spacing={showMoreFields ? 0 : "md"} style={{ flexGrow: 1, width: "100%" }}>
                {attemptsLeft < reAttempts && !isVerifying && (
                  <Alert
                    mt="xl"
                    mb="md"
                    title="Whoops! That did not go as expected"
                    styles={{
                      root: {
                        backgroundColor:
                          theme.colorScheme === "dark"
                            ? theme.colors.red[9]
                            : theme.colors.red[0],
                        color:
                          theme.colorScheme === "dark"
                            ? theme.colors.red[0]
                            : theme.colors.red[9],
                      },
                      title: {
                        fontSize: "md",
                        color: theme.colorScheme === "dark" ? theme.white : theme.black,
                      },
                      message: {
                        fontSize: "sm",
                        color: theme.colorScheme === "dark" ? theme.white : theme.black,
                      },
                    }}
                  >
                    Please check that you've entered your full legal name, and provided an accurate date of birth or SSN.
                  </Alert>
                )}

                {Object.keys(groupedFields.grouped).map((group) => (
                  <MantineGroup key={group} spacing="md" noWrap grow>
                    {groupedFields.grouped[group].map((field) => (
                      <FormField
                        key={field.name}
                        field={field}
                        form={form}
                        settings={settings}
                        componentKey={componentKey}
                        showMoreFields={showMoreFields}
                        onDobChange={handleDobChange}
                        onAddressChange={handleAddressChange}
                      />
                    ))}
                  </MantineGroup>
                ))}

                {/* Name fields - hide when expanded */}
                <div
                  ref={nameFieldsRef}
                  style={{
                    overflow: "hidden",
                    maxHeight: showMoreFields
                      ? 0
                      : nameFieldsVisible
                        ? nameFieldsHeight || "none"
                        : 0,
                    opacity: showMoreFields ? 0 : nameFieldsVisible ? 1 : 0,
                    transform: showMoreFields
                      ? "translateY(-20px)"
                      : nameFieldsVisible
                        ? "translateY(0)"
                        : "translateY(20px)",
                    transition: `max-height 0.5s ${bounceTransition}, opacity 0.5s ${bounceTransition}, transform 0.5s ${bounceTransition}`,
                    animation:
                      !showMoreFields && nameFieldsVisible
                        ? `slideUpBounce 0.5s ${bounceTransition}`
                        : "none",
                    pointerEvents: showMoreFields ? "none" : "auto",
                  }}
                >
                  <Stack spacing="md">
                    {primaryFields.map((field) => (
                      <FormField
                        key={field.name}
                        field={field}
                        form={form}
                        settings={settings}
                        componentKey={componentKey}
                        showMoreFields={showMoreFields}
                        onDobChange={handleDobChange}
                        onAddressChange={handleAddressChange}
                      />
                    ))}
                  </Stack>
                </div>

                {/* Expandable container that grows from button */}
                {additionalFields.length > 0 && (
                  <div
                    style={{
                      overflow: "hidden",
                      maxHeight: showMoreFields ? additionalHeight : 0,
                      opacity: showMoreFields ? 1 : 0,
                      transform: showMoreFields ? "translateY(0)" : "translateY(20px)",
                      transition: `max-height 0.5s ${bounceTransition}, opacity 0.5s ${bounceTransition}, transform 0.5s ${bounceTransition}`,
                      animation: showMoreFields
                        ? `slideUpBounce 0.5s ${bounceTransition}`
                        : "none",
                      pointerEvents: showMoreFields ? "auto" : "none",
                    }}
                  >
                    <div ref={additionalFieldsRef}>
                      <Stack spacing="md">
                        {additionalFields.map((field) => (
                          <FormField
                            key={field.name}
                            field={field}
                            form={form}
                            settings={settings}
                            componentKey={componentKey}
                            showMoreFields={showMoreFields}
                            onDobChange={handleDobChange}
                            onAddressChange={handleAddressChange}
                          />
                        ))}
                        
                        {/* Back button when showing additional fields */}
                        <div
                          style={{
                            textAlign: "center",
                            paddingTop: "20px",
                            transform: showMoreFields ? "scale(1)" : "scale(0.9)",
                            opacity: showMoreFields ? 1 : 0,
                            transition: `opacity 0.5s ${bounceTransition}, transform 0.5s ${bounceTransition}`,
                            pointerEvents: showMoreFields ? "auto" : "none",
                          }}
                        >
                          <Button
                            variant="subtle"
                            size="sm"
                            onClick={() => setShowMoreFields(false)}
                            sx={{
                              color:
                                theme.colorScheme === "dark"
                                  ? theme.colors.gray[3]
                                  : theme.colors.gray[7],
                              "&:hover": {
                                backgroundColor:
                                  theme.colorScheme === "dark"
                                    ? theme.colors.dark[6]
                                    : theme.colors.gray[0],
                              },
                            }}
                            styles={{
                              root: {
                                fontSize: "13px",
                                height: "32px",
                                padding: "0 16px",
                              },
                            }}
                          >
                            ← Back
                          </Button>
                        </div>
                      </Stack>
                    </div>
                  </div>
                )}
              </Stack>

              {/* Button positioned at bottom */}
              <Box mt="md" pb="15px" mb="80px" style={{ width: "100%" }}>
                <Button
                  mt="xl"
                  fullWidth
                  size="lg"
                  type={showMoreFields ? "submit" : "button"}
                  disabled={
                    isVerifying ||
                    (!showMoreFields && !isNameFieldsValid()) ||
                    (showMoreFields && !isCoppaDetailsValid())
                  }
                  loading={false}
                  onClick={(e) => {
                    if (!showMoreFields) {
                      e.preventDefault();
                      setShowMoreFields(true);
                    }
                  }}
                  sx={{
                    transition: `all 0.5s ${bounceTransition}`,
                    "&:hover": { transform: "scale(1.01)" },
                    '&[data-loading="true"]': { color: buttonContentColor },
                  }}
                >
                  {isVerifying ? (
                    <MantineGroup spacing="xs" noWrap>
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid transparent",
                          borderTop: `2px solid ${buttonContentColor}`,
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                      <Text c="inherit">Sending Data</Text>
                    </MantineGroup>
                  ) : showMoreFields ? (
                    buttonText
                  ) : (
                    "Add more details"
                  )}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </form>
    </>
  );
};

export default CoppaForm;